import { BbSelectOption } from 'app/components/shared/bb-select/bb-select.component';
import { InteractivityDesignDimensionsService, DesignDimensions } from './../../services/interactivity-design-dimensions.service';
import { Component, ElementRef, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { StageWidget } from '../../classes/StageWidget';
import { Subscription } from 'rxjs';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { WidgetType } from '../../enums/WidgetType';
import { ReduxService } from '../../../../../services/redux.service';
import { TimelineDataActions } from '../../redux/timeline-data-actions';
import { hex2rgba } from 'app/shared/utils/hex2rgba.util';

@Component({
	selector: 'stage-widget',
	templateUrl: './stage.widget.component.html',
	styleUrls: ['./stage.widget.component.scss'],
	host: {
		'(click)': 'log(widget)'
	}
})

export class StageWidgetComponent implements OnInit {
	@Input() widget: StageWidget;
	@Input() isHovered = false;
	@Output() editModeChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

	flexAlignItemsMap = {
		top: 'flex-start',
		center: 'center',
		bottom: 'flex-end'
	};
	clickTimeout: any;
	private designHeight: number;
	private designWidth: number;
	private subscriptions: Subscription = new Subscription();

	private _contentEditable = false;
	set contentEditable(val: boolean) {
		this._contentEditable = val;
		this.editModeChanged.emit(val);
	}

	get contentEditable() {
		return this._contentEditable;
	}

	constructor(
		private el: ElementRef,
		private interactivityEditorService: InteractivityEditorService,
		private interactivityDesignDimensionsService: InteractivityDesignDimensionsService,
		private reduxService: ReduxService
	) {
	}

	ngOnInit() {
		this.widget.elRef = this.el;

		this.subscriptions.add(this.interactivityDesignDimensionsService.designDimensionsSubject.subscribe((designDimensions: DesignDimensions) => {
			this.designHeight = designDimensions.height * designDimensions.zoomFactor;
			this.designWidth = designDimensions.width * designDimensions.zoomFactor;
		}));
	}

	pct2px(pctString: string, onWidth = false): string {
		if (typeof pctString === 'string') {
			const pct = Number(pctString.replace('%', ''));
			let ret;
			if (onWidth) {
				ret = (pct / 100) * this.designWidth;
			} else {
				ret = (pct / 100) * this.designHeight;
			}
			return ret + 'px';
		}
	}

	getMargin(widget: StageWidget) {
		const marginsPx: string[] = [];
		if (typeof widget.config?.textFormat?.textFormat?.margin === 'string') {
			const pctParts = widget.config.textFormat.textFormat.margin.split(';');
			pctParts.forEach((pctPart: string, idx: number) => {
				marginsPx.push(this.pct2px(pctPart, !!(idx % 2))); // onWidth true for odd idx
			});
		}
		if (widget.type === WidgetType.FormSelect) {
			// ignore top and bottom margins for select widgets
			marginsPx[0] = '0';
			marginsPx[2] = '0';
		}
		return marginsPx.join(' ');
	}

	getOpacity(widget: StageWidget) {
		let opacity = 1;
		if (widget && widget.calculatedConfig && typeof widget.calculatedConfig.alpha === 'string') {
			opacity = Number(widget.calculatedConfig.alpha.replace('%', '')) / 100;
		}
		return opacity;
	}

	getBorderStyle(widget: StageWidget) {
		return ((widget && widget.config && this.pct2px(widget.config.borderthickness)) || 0) + ' solid ' + this.getColor(widget, 'border');
	}

	getFontSize(widget: StageWidget) {
		return (widget.config?.textFormat?.textFormat?.fontsize) ?
			this.pct2px(widget.config.textFormat.textFormat.fontsize) : 'inherit';
	}

	getLineHeight(widget: StageWidget) {
		return (widget.config?.textFormat?.textFormat?.fontsize) ?
			this.pct2px(String(parseFloat(widget.config.textFormat.textFormat.fontsize) * 1.2)) : 'inherit';
	}

	getPlaceHolderTextValue(widget: StageWidget): string {
		let phText: any;
		switch (widget.type) {
			case 'formTextField':
			case 'formSelect':
			case 'formTextArea':
				if (widget.config && widget.config.placeHolder) {
					phText = widget.config.placeHolder;
				} else {
					return '';
				}
				break;
			default:
				break;
		}
		return this.processPropertyTextValue(phText);
	}

	getTextValue(widget: StageWidget) {
		let txt: any;
		switch (widget.type) {
			case 'textLabel':
			case 'button':
				if (widget.config && widget.config.text) {
					txt = widget.config.text;
				} else {
					return '';
				}
				break;
			case 'formTextField':
			case 'formTextArea':
				if (widget.config && widget.config.value) {
					txt = widget.config.value;
				} else {
					return '';
				}
				break;
			case 'formSelect':
				const selectedOption = widget.config.options.find((option: any) => option.value === widget.config?.value);
				txt = selectedOption?.label || widget.config.value;
				break;
			default:
				break;
		}
		return this.processPropertyTextValue(txt);
	}

	optionTrackBy(index: number, option: BbSelectOption) {
		return option?.value || index;
	}

	processPropertyTextValue(txt: any, reversed: boolean = false): string {
		if (txt.datasource) {
			// check if txt comes from datasource, if so, assign defaultvalue.
			txt = txt.datasource.defaultValue;
		}
		if (typeof txt !== 'string') {
			return '';
		}
		const newTxt = reversed ? txt.replace(/<br\s*\/?>/gi, '\n') : txt.split('\n').join('<br />');
		return newTxt;
	}

	getColor(widget: StageWidget, property: 'background' | 'border') {
		let color = 'transparent';
		switch (property) {
			case 'background':
				if (widget.type === WidgetType.Rectangle) {
					color = '#' + widget.config.color;
				} else {
					const alpha = (widget && widget.config && widget.config.background_alpha) ?
						widget.config.background_alpha : widget && widget.config && widget.config.background === 'true' ? 1 : 0;
					if (widget && widget.config && typeof widget.config.backgroundcolor === 'string') {
						color = hex2rgba('#' + widget.config.backgroundcolor, alpha);
					}
				}


				break;

			case 'border':
				if (widget && widget.config && typeof widget.config.bordercolor === 'string') {
					color = '#' + widget.config.bordercolor;
				} else {
					color = 'transparent';
				}
				break;

		}

		return color;
	}


	getFontFamily(widget: StageWidget) {
		if (widget?.config?.textFormat?.textFormat?.font) {

			const textFormat = widget.config.textFormat.textFormat;

			return (typeof textFormat.embeddedfont === 'undefined' || parseFloat(textFormat.embeddedfont) < 0) ?
				textFormat.font : 'bb-select-' + textFormat.font;
		} else {
			return 'Verdana';
		}
	}

	onPaste(ev: any) {
		// get text without HTML
		const text = (ev.originalEvent || ev).clipboardData.getData('text/plain');
		ev.preventDefault();
		ev.stopPropagation();
		// paste in position:
		document.execCommand('insertText', false, text);
	}

	onEditMousedown(ev: MouseEvent) {
		if (this.contentEditable && !this.clickTimeout) {
			// ev.preventDefault();
			ev.stopPropagation();
		}
	}

	onMouseWheel(ev: Event) {
		if (this.contentEditable && !this.clickTimeout) {
			ev.stopPropagation();
		}
	}

	onEditMouseup(ev: MouseEvent) {
		// using mouseup instead of dblclick so we directly have the cursor where you doubleclick
		if (!this.contentEditable || this.clickTimeout) {
			this.contentEditable = true;
			if (this.clickTimeout) {
				clearTimeout(this.clickTimeout);
				this.clickTimeout = null;
			} else {
				this.clickTimeout = setTimeout(() => {
					this.contentEditable = false;
					this.clickTimeout = null;
				}, 350);
			}
		}
	}

	onBlur(text: string) {
		this.onContentChanged(text);
	}

	onContentChanged(text: string) {
		if (this.contentEditable) {
			// update
			let newConfig: any;
			switch (this.widget.type) {
				case 'textLabel':
				case 'button':
					newConfig = { text };
					break;
				case 'formTextArea':
				case 'formTextField':
				case 'formSelect':
					newConfig = { value: text };
					break;
				default:
					break;
			}
			this.reduxService.dispatch({
				type: TimelineDataActions.CHANGE_WIDGET_CONFIG,
				id: this.widget.id,
				config: { ...this.widget.config, ...newConfig },
				stateSet: this.interactivityEditorService.activeStateSet,
				state: this.interactivityEditorService.activeState,
				description: 'Changed text of widget: ' + this.widget.label
			});
			this.contentEditable = false;
		}

	}

	log(item: any) {
		console.warn('LOG: item = ', item);
	}
}
